import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, FileTextIcon, ShieldCheckIcon } from 'lucide-react';
const docs = [{
  name: 'TRIA Form',
  file: 'TRIA_Form.pdf',
  status: 'verified',
  fields: [['Coverage', 'Accepted'], ['Premium', '$1,250.00'], ['Signed', 'Yes'], ['Signed Date', '2025-03-27']]
}, {
  name: 'ACORD 125',
  file: 'ACORD_125.pdf',
  status: 'flagged',
  fields: [['Applicant Name', 'Isai Melendez'], ['Effective Date', '2025-05-22'], ['Producer Signed', 'No', 'flag'], ['Applicant Signed', 'No', 'flag']]
}];
const CYCLE = 6; // seconds
const docTiming = [{
  scanStart: 0.04,
  scanEnd: 0.22,
  statusIn: 0.26
}, {
  scanStart: 0.24,
  scanEnd: 0.42,
  statusIn: 0.46
}];
const STATUS_OUT = 0.86;
// Color tokens
const COLOR_BG_NEUTRAL = 'rgba(24, 24, 27, 0.6)';
const COLOR_BG_AMBER = 'rgba(245, 158, 11, 0.15)';
const COLOR_BORDER_NEUTRAL = 'rgba(255, 255, 255, 0.05)';
const COLOR_BORDER_AMBER = 'rgba(245, 158, 11, 0.3)';
const COLOR_TEXT_NEUTRAL = 'rgb(228, 228, 231)'; // zinc-200
const COLOR_TEXT_AMBER = 'rgb(251, 191, 36)'; // amber-400
export function ValidationMock() {
  return <div className="rounded-2xl border border-white/10 bg-zinc-900/80 p-4 shadow-2xl shadow-black/40 sm:p-5" data-id="element-453">
      {/* Header — compact on mobile, full on desktop */}
      <div className="mb-4 flex items-center justify-between gap-3" data-id="element-454">
        <div className="min-w-0" data-id="element-455">
          <div className="truncate text-[13px] font-semibold text-white sm:text-sm" data-id="element-456">
            <span className="hidden sm:inline" data-id="element-457">
              Seaview Systems · POL-112312333
            </span>
            <span className="sm:hidden" data-id="element-458">POL-112312333</span>
          </div>
          <div className="text-[10px] text-zinc-500 sm:text-[11px]" data-id="element-459">
            <span className="hidden sm:inline" data-id="element-460">
              Submitted Apr 21, 2026 · Jane Lewis
            </span>
            <span className="sm:hidden" data-id="element-461">Apr 21 · Jane Lewis</span>
          </div>
        </div>
        <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-accent/20 bg-accent/10 px-2 py-1 text-[10px] font-medium text-accent" data-id="element-462">
          <motion.span animate={{
          scale: [1, 1.15, 1],
          opacity: [0.7, 1, 0.7]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-flex" data-id="element-463">
            <ShieldCheckIcon className="h-3 w-3" data-id="element-464" />
          </motion.span>
          AI Reviewed
        </span>
      </div>

      {/* MOBILE — compact rows: each doc shows scan sweep → status → (issues only if flagged) */}
      <div className="space-y-2 sm:hidden" data-id="element-465">
        {docs.map((doc, idx) => {
        const t = docTiming[idx];
        const flaggedFields = doc.fields.filter(f => f[2] === 'flag');
        const isVerified = doc.status === 'verified';
        return <div key={doc.name} className="relative overflow-hidden rounded-xl border border-white/5 bg-zinc-950/60 px-3.5 py-3" data-id="element-466">
              {/* Scanning sweep */}
              <motion.div aria-hidden animate={{
            y: ['-10%', '-10%', '110%', '110%'],
            opacity: [0, 1, 1, 0]
          }} transition={{
            duration: CYCLE,
            times: [0, t.scanStart, t.scanEnd, 1],
            repeat: Infinity,
            repeatType: 'loop',
            ease: 'linear'
          }} className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/70 to-transparent" data-id="element-467" />

              {/* Top row: doc name + animated status pill */}
              <div className="flex items-center gap-2" data-id="element-468">
                <FileTextIcon className="h-4 w-4 flex-shrink-0 text-zinc-500" data-id="element-469" />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-white" data-id="element-470">
                  {doc.name}
                </span>
                <motion.span animate={{
              scale: [0.8, 0.8, 1, 1, 1],
              opacity: [0, 0, 1, 1, 0]
            }} transition={{
              duration: CYCLE,
              times: [0, t.statusIn - 0.03, t.statusIn + 0.01, STATUS_OUT, 1],
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut'
            }} className={isVerified ? 'inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400' : 'inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400'} data-id="element-471">
                  {isVerified ? <>
                      <CheckCircle2Icon className="h-2.5 w-2.5" data-id="element-472" /> Verified
                    </> : <>
                      <AlertCircleIcon className="h-2.5 w-2.5" data-id="element-473" />{' '}
                      {flaggedFields.length} issues
                    </>}
                </motion.span>
              </div>

              {/* Scanning meta line — visible during scan, fades out for status */}
              <div className="mt-1.5 h-3 pl-6" data-id="element-474">
                <motion.div animate={{
              opacity: [0, 0, 1, 1, 0, 0]
            }} transition={{
              duration: CYCLE,
              times: [0, t.scanStart - 0.01, t.scanStart, t.scanEnd, t.scanEnd + 0.01, 1],
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'linear'
            }} className="font-mono text-[9px] uppercase tracking-[0.16em] text-accent" data-id="element-475">
                  Scanning {doc.fields.length} fields…
                </motion.div>
              </div>

              {/* Resolution: verified → "X fields validated" / flagged → list specific issues */}
              <motion.div animate={{
            opacity: [0, 0, 1, 1, 0]
          }} transition={{
            duration: CYCLE,
            times: [0, t.statusIn - 0.02, t.statusIn + 0.02, STATUS_OUT, 1],
            repeat: Infinity,
            repeatType: 'loop',
            ease: 'easeInOut'
          }} className="mt-1 pl-6" data-id="element-476">
                {isVerified ? <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500" data-id="element-477">
                    {doc.fields.length} fields validated
                  </div> : <ul className="space-y-0.5" data-id="element-478">
                    {flaggedFields.map(([k, v]) => <li key={k} className="flex items-center gap-1.5 font-mono text-[10px] text-amber-300/90" data-id="element-479">
                        <span className="h-1 w-1 rounded-full bg-amber-400" data-id="element-480" />
                        <span className="text-zinc-300" data-id="element-481">{k}:</span>
                        <span className="text-amber-400" data-id="element-482">{v}</span>
                      </li>)}
                  </ul>}
              </motion.div>
            </div>;
      })}
      </div>

      {/* DESKTOP — full doc cards with field grid */}
      <div className="hidden space-y-3 sm:block" data-id="element-483">
        {docs.map((doc, idx) => {
        const t = docTiming[idx];
        return <div key={doc.name} className="relative overflow-hidden rounded-xl border border-white/5 bg-zinc-950/60 p-4" data-id="element-484">
              <motion.div aria-hidden animate={{
            y: ['-10%', '-10%', '110%', '110%'],
            opacity: [0, 1, 1, 0]
          }} transition={{
            duration: CYCLE,
            times: [0, t.scanStart, t.scanEnd, 1],
            repeat: Infinity,
            repeatType: 'loop',
            ease: 'linear'
          }} className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" data-id="element-485" />

              <div className="mb-3 flex items-center gap-2" data-id="element-486">
                <FileTextIcon className="h-4 w-4 text-zinc-500" data-id="element-487" />
                <span className="text-sm font-medium text-white" data-id="element-488">
                  {doc.name}
                </span>
                <span className="text-[11px] text-zinc-500" data-id="element-489">· {doc.file}</span>
                <div className="ml-auto" data-id="element-490">
                  <motion.span animate={{
                scale: [0.8, 0.8, 1, 1, 1],
                opacity: [0, 0, 1, 1, 0]
              }} transition={{
                duration: CYCLE,
                times: [0, t.statusIn - 0.03, t.statusIn + 0.01, STATUS_OUT, 1],
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut'
              }} className={doc.status === 'verified' ? 'inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400' : 'inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-400'} data-id="element-491">
                    {doc.status === 'verified' ? <>
                        <CheckCircle2Icon className="h-3 w-3" data-id="element-492" /> Verified
                      </> : <>
                        <AlertCircleIcon className="h-3 w-3" data-id="element-493" /> Needs review
                      </>}
                  </motion.span>
                </div>
              </div>

              <div className="grid gap-1.5 sm:grid-cols-2" data-id="element-494">
                {doc.fields.map(([k, v, flag], fIdx) => {
              const flagIn = t.statusIn + 0.03 + fIdx * 0.02;
              return <motion.div key={k} animate={flag ? {
                backgroundColor: [COLOR_BG_NEUTRAL, COLOR_BG_NEUTRAL, COLOR_BG_AMBER, COLOR_BG_AMBER, COLOR_BG_NEUTRAL],
                borderColor: [COLOR_BORDER_NEUTRAL, COLOR_BORDER_NEUTRAL, COLOR_BORDER_AMBER, COLOR_BORDER_AMBER, COLOR_BORDER_NEUTRAL]
              } : undefined} transition={flag ? {
                duration: CYCLE,
                times: [0, t.statusIn, flagIn, STATUS_OUT, 1],
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut'
              } : undefined} style={flag ? {
                backgroundColor: COLOR_BG_NEUTRAL,
                borderColor: COLOR_BORDER_NEUTRAL
              } : undefined} className="flex items-center justify-between rounded-md border border-white/5 bg-zinc-900/60 px-2.5 py-1.5 text-xs" data-id="element-495">
                      <span className="text-zinc-500" data-id="element-496">{k}</span>
                      <motion.span animate={flag ? {
                  color: [COLOR_TEXT_NEUTRAL, COLOR_TEXT_NEUTRAL, COLOR_TEXT_AMBER, COLOR_TEXT_AMBER, COLOR_TEXT_NEUTRAL]
                } : undefined} transition={flag ? {
                  duration: CYCLE,
                  times: [0, t.statusIn, flagIn, STATUS_OUT, 1],
                  repeat: Infinity,
                  repeatType: 'loop',
                  ease: 'easeInOut'
                } : undefined} className="font-mono text-[11px] text-zinc-200" data-id="element-497">
                        {v}
                      </motion.span>
                    </motion.div>;
            })}
              </div>
            </div>;
      })}
      </div>
    </div>;
}